import { useState } from "react";
import { Search } from "lucide-react";
import { useAdminDianProcesses } from "@/lib/api/dian-processes.queries";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { Table, THead, TH, TBody, TR, TD, EmptyState } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { DianStatusBadge } from "@/components/ui/Badge";
import { formatDate } from "@/lib/utils";

export function ProcessesList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [companyFilter, setCompanyFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [docTypeFilter, setDocTypeFilter] = useState("");

  const query = useAdminDianProcesses({
    companyId: companyFilter || undefined,
    status: statusFilter || undefined,
    documentType: docTypeFilter || undefined,
    page,
    pageSize,
  });

  const total = query.data?.pagination.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Procesos DIAN</h1>
        <p className="text-sm text-slate-500 mt-1">Vista transversal de todos los procesos de emisión DIAN.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative min-w-[180px]">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={companyFilter}
            onChange={(e) => { setCompanyFilter(e.target.value); setPage(1); }}
            placeholder="NIT empresa"
            className="pl-8"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <option value="">Todos los estados</option>
          <option value="queued">En cola</option>
          <option value="processing">Procesando</option>
          <option value="completed">Completado</option>
          <option value="failed">Fallido</option>
        </select>
        <select
          value={docTypeFilter}
          onChange={(e) => { setDocTypeFilter(e.target.value); setPage(1); }}
          className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <option value="">Todos los tipos</option>
          <option value="sales-invoice">Factura venta</option>
          <option value="credit-note">Nota crédito</option>
          <option value="debit-note">Nota débito</option>
          <option value="pos">Documento POS</option>
          <option value="support-document">Documento soporte</option>
          <option value="payroll">Nómina</option>
        </select>
      </div>

      <Card className="overflow-hidden">
        {query.isPending ? (
          <CenteredSpinner label="Cargando procesos…" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : (query.data?.processes ?? []).length === 0 ? (
          <EmptyState title="Sin procesos" description="No hay procesos con los filtros seleccionados." />
        ) : (
          <>
            <Table>
              <THead>
                <tr>
                  <TH>Process ID</TH>
                  <TH>NIT empresa</TH>
                  <TH>Tipo</TH>
                  <TH>Documento</TH>
                  <TH>Estado</TH>
                  <TH>Creado</TH>
                  <TH>Actualizado</TH>
                </tr>
              </THead>
              <TBody>
                {query.data.processes.map((p) => (
                  <TR key={p.processId}>
                    <TD className="font-mono text-[11px]">{p.processId}</TD>
                    <TD className="font-mono text-xs">{p.companyNit}</TD>
                    <TD className="text-xs">{p.documentType}</TD>
                    <TD className="font-mono text-[11px]">{p.sourceDocumentId ?? "—"}</TD>
                    <TD><DianStatusBadge status={p.status} /></TD>
                    <TD className="text-[11px] text-slate-500">{formatDate(p.createdAt)}</TD>
                    <TD className="text-[11px] text-slate-500">{formatDate(p.updatedAt)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
              <p className="text-xs text-slate-600">
                {total} total · página {page} de {pageCount}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Anterior
                </Button>
                <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}>
                  Siguiente
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
